import env from './env'
import { getLoginInfo } from './http'

/**
 * 获取服务器文件的完整URL
 * @param {*} fileFolder 文件夹，可以为空
 * @param {*} filename 文件名，可以为空
 * @param {*} shared 是否是共享文件
 * @returns 资源服务器的完整URL
 */
export function getUrl(fileFolder, filename, shared = false) {
  const baseUrl = env.RESOURCE_READ_URL
  const bucketName = shared ? 'preset' : getLoginInfo().bucketName
  const folder = fileFolder ? fileFolder + '/' : ''
  return filename ? baseUrl + bucketName + '/' + folder + filename : ''
}

/**
 * 获取服务器文件的相对URL（组成：存储桶+文件夹+文件名）
 * @param {*} fileFolder 文件夹
 * @param {*} filename 文件名
 * @param {*} shared 是否是共享文件
 * @returns 资源服务器的相对URL
 */
export function getPath(fileFolder, filename, shared = false) {
  const bucketName = shared ? 'preset' : getLoginInfo().bucketName
  const folder = fileFolder + '/'
  return bucketName + '/' + folder + filename
}

/**
 * 获取服务器文件的前缀（组成：存储桶+文件夹）
 * @param {*} fileFolder 文件夹，不能为空
 * @param {*} shared 是否是共享文件
 * @returns 文件的前缀,结尾带'/'
 */
export function getPrefix(fileFolder, shared = false) {
  const bucketName = shared ? 'preset' : getLoginInfo().bucketName
  return bucketName + '/' + fileFolder + '/'
}

const officeExtensions = new Set(['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'])

/**
 * 解析文件名
 * @param {String} filename
 */
export function parseFilename(filename) {
  let stem = null
  let ext = null
  if (!filename) return { stem, ext }
  const dot = filename.lastIndexOf('.')
  if (dot === -1) {
    stem = filename
  } else {
    stem = filename.substring(0, dot)
    ext = dot === filename.length - 1 ? null : filename.substring(dot + 1).toLowerCase()
  }
  return { stem, ext }
}

export function getPreviewUrl(fileFolder, filename, shared = false) {
  const { stem, ext } = parseFilename(filename)
  const name = officeExtensions.has(ext) ? stem + '.pdf' : filename
  return getUrl(fileFolder, name, shared)
}

/**
 * 目标既非当前页所在源、也非 `VITE_RESOURCE_READ_URL` 资源站时，视为第三方站点：不在此做 fetch 预检（浏览器对第三方通常无 CORS，预检无意义），直接视为「无法判断」。
 * @param {string} url
 * @returns {boolean}
 */
function isUntrustedHttpOrigin(url) {
  if (typeof window === 'undefined') return false
  try {
    const u = new URL(url, window.location.href)
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return false
    const trusted = new Set([window.location.origin])
    const base = env.RESOURCE_READ_URL
    if (base) {
      try {
        trusted.add(new URL(base, window.location.href).origin)
      } catch {
        /* ignore */
      }
    }
    return !trusted.has(u.origin)
  } catch {
    return false
  }
}

/**
 * 用 fetch 探测资源 URL 是否可正常拉取（常用于预览前避免 iframe 展示 OSS/S3 错误 XML）。
 *
 * @param {string} url
 * @returns {Promise<boolean|null>} true 可用，false 确定不可用，null 无法判断（第三方/CORS/网络等）
 */
export async function verifyResourceUrl(url) {
  if (!url) return false
  if (isUntrustedHttpOrigin(url)) return null
  try {
    let res = await fetch(url, { method: 'HEAD', mode: 'cors', cache: 'no-store' })
    if (res.ok) return true
    if (res.status === 404 || res.status === 403) return false
    if (res.status === 405) {
      res = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-store',
        headers: { Range: 'bytes=0-4095' }
      })
    } else if (res.status >= 400 && res.status < 500) {
      return false
    } else {
      res = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-store',
        headers: { Range: 'bytes=0-4095' }
      })
    }

    if (res.status === 404 || res.status === 403) return false
    if (!(res.ok || res.status === 206)) return false

    const ct = (res.headers.get('content-type') || '').toLowerCase()
    if (ct.includes('xml') || (ct.includes('text/') && !ct.includes('pdf'))) {
      const text = await res.text()
      if (/nosuchkey|specified key does not exist|accessdenied|noSuchBucket/i.test(text)) return false
      if (text.trimStart().startsWith('<?xml') && /<Error[\s>]/i.test(text)) return false
    }
    return true
  } catch {
    return null
  }
}

const words = ['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']

const ppts = ['application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation']

const excels = ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']

const offices = [...words, ...ppts, ...excels]

export function isOffice(mime) {
  return offices.includes(mime)
}

export function canPreview(mime) {
  return mime === 'application/pdf' || mime === 'video/mp4' || offices.includes(mime) || mime.startsWith('image/') || mime.startsWith('text/')
}

export function mime2name(mime) {
  if (!mime) return ''
  if (mime === 'application/pdf') return 'PDF'
  if (mime === 'video/mp4') return '视频'
  if (words.includes(mime)) return 'Word'
  if (ppts.includes(mime)) return 'PPT'
  if (excels.includes(mime)) return 'Excel'
  if (mime.startsWith('image/')) return '图片'
  if (mime.startsWith('text/')) return '文本'
  return '其他'
}

/**
 * 服务器端资源文件夹
 */
export const fileFolder = {
  /**
   * 首页logo
   */
  HOME_LOGO: 'home',
  /**
   * 四色图图片(预览图、背景图、嵌入式图片)
   */
  MAP_IMAGE: 'map-image',
  /**
   * 风险管控措施描述图片
   */
  RISK_CTRL_MEASURE_DESC_IMGS: 'risk-ctrl-measure-desc-imgs',
  /**
   * 隐患描述图片/整改图片
   */
  HAZARD_IMAGE: 'hazard-image',
  /**
   * 特种设备附件
   */
  SPECIAL_EQUIP_ATTACH: 'special-equip-attach',
  /**
   * 票据关联文档
   */
  TICKET_RESOURCES: 'ticket-resources',
  /**
   * 签名文件
   */
  SIGN_IMAGE: 'sign-image',
  /**
   * 违规违章描述图片
   */
  VIOLATION_DESCRIPTION_IMAGES: 'violation-description-images',
  /**
   * 线下培训资源
   */
  OFFLINE_TRAIN_RESOURCES: 'offline-train-resources',
  /**
   * 现场照片
   */
  LIVE_IMAGE: 'live-image',
  /**
   * 常规线上培训资源
   */
  TRAIN_RESOURCES: 'train-resources',
  /**
   * 共享学习资源
   */
  SHARED_RESOURCES: 'resources',
  /**
   * 公司资源
   */
  COMPANY_RESOURCES: 'company-resources',
  /**
   * 风险评估培训用户手动上传的资源
   */
  RISK_TRAIN_RESOURCES: 'risk-train-resources',
  /**
   * 检查表检查项描述图片
   */
  CHECK_ITEM_IMAGE: 'check-item-image',
  /**
   * 考题描述图片
   */
  QUESTION_IMAGE: 'questions',
  /**
   * 积分商城商品图片
   */
  SCORE_GOODS_IMAGE: 'score-goods-image'
}

export default {
  getUrl,
  getPath,
  getPrefix,
  parseFilename,
  getPreviewUrl,
  verifyResourceUrl,
  isOffice,
  canPreview,
  mime2name,
  fileFolder
}
